// Pure transform helpers extracted from the former zustand campaigns store so
// that `slice/emailCampaignsSlice.jsx` can reuse them unchanged.
const normalizeDateValue = (value) => {
  if (!value) return null;
  try {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toISOString();
  } catch {
    return null;
  }
};

const deriveScheduledAt = (campaign) => {
  return (
    normalizeDateValue(campaign.scheduledAt) ||
    normalizeDateValue(campaign.scheduling?.scheduledAt) ||
    normalizeDateValue(campaign.scheduleDate)
  );
};

const deriveSentAt = (campaign) => {
  const direct = normalizeDateValue(campaign.sentAt);
  if (direct) return direct;

  if (Array.isArray(campaign.recipients)) {
    const timestamps = campaign.recipients
      .map(r => normalizeDateValue(r.sentAt || r.deliveredAt))
      .filter(Boolean)
      .map(ts => new Date(ts).getTime());
    if (timestamps.length) {
      return new Date(Math.min(...timestamps)).toISOString();
    }
  }

  if (campaign.status && campaign.status.toLowerCase() === 'sent') {
    return normalizeDateValue(campaign.updatedAt || campaign.createdAt);
  }

  return null;
};

export const sample = [
  { 
    id: 'camp1', 
    name: 'Welcome Series', 
    subject: 'Welcome to our amazing service!', 
    preheader: 'Get started with these helpful tips',
    fromName: 'Welcome Team',
    fromEmail: 'welcome@company.com',
    content: '<h2>Welcome {{firstName}}!</h2><p>We\'re excited to have you join our community.</p><ul><li>Explore our features</li><li>Connect with other users</li><li>Get personalized recommendations</li></ul><p>Best regards,<br/>{{companyName}}</p>',
    audience: { type: 'all', filters: [], segments: [] },
    abTest: { enabled: false },
    schedule: { sendOption: 'now' },
    status: 'Sent', 
    createdAt: new Date().toISOString(), 
    sentAt: new Date().toISOString(),
    scheduledAt: null,
    templateId: 't1',
    metrics: { sent: 1250, opens: 825, clicks: 156, conversions: 23 }
  },
  { 
    id: 'camp2', 
    name: 'Black Friday Mega Sale', 
    subject: '🔥 50% OFF Everything - Limited Time!', 
    preheader: 'Biggest sale of the year ends tonight',
    fromName: 'Sales Team',
    fromEmail: 'offers@company.com',
    content: '<h1>🎉 BLACK FRIDAY SALE</h1><p>Hi {{firstName}},</p><p>Don\'t miss out on our biggest sale of the year!</p><ul><li>50% off all products</li><li>Free shipping worldwide</li><li>Exclusive member bonuses</li></ul><p><a href="#">Shop Now</a></p>',
    audience: { type: 'segments', filters: [], segments: ['VIP Customers', 'High Engagement'] },
    abTest: { 
      enabled: true, 
      variants: {
        A: { subject: '🔥 50% OFF Everything - Limited Time!', content: 'Variant A content' },
        B: { subject: '💸 HUGE Black Friday Savings!', content: 'Variant B content' }
      },
      splitPercentage: 50
    },
    schedule: { sendOption: 'schedule', scheduleDate: '2024-11-29', scheduleTime: '09:00', timezone: 'America/New_York' },
    status: 'Scheduled', 
    createdAt: new Date().toISOString(), 
    scheduledAt: '2024-11-29T09:00:00.000Z',
    sentAt: null,
    templateId: 't2',
    metrics: { sent: 0, opens: 0, clicks: 0, conversions: 0 }
  },
];

// Helper functions for campaign processing
const transformBackendScheduleToFrontend = (scheduling) => {
  if (!scheduling) return { sendOption: 'now' };
  
  switch (scheduling.type) {
    case 'immediate':
      return { sendOption: 'now' };
    case 'scheduled':
      // Convert scheduledAt back to separate date and time fields
      if (scheduling.scheduledAt) {
        const scheduledDate = new Date(scheduling.scheduledAt);
        const scheduleDate = scheduledDate.toISOString().split('T')[0]; // YYYY-MM-DD
        const scheduleTime = scheduledDate.toTimeString().split(' ')[0].slice(0, 5); // HH:MM
        return {
          sendOption: 'schedule',
          scheduleDate,
          scheduleTime,
          timezone: scheduling.timezone || 'UTC'
        };
      }
      return {
        sendOption: 'schedule',
        scheduleDate: '',
        scheduleTime: '',
        timezone: scheduling.timezone || 'UTC'
      };
    case 'optimized':
      return { sendOption: 'optimize' };
    default:
      return { sendOption: 'now' };
  }
};

const processAudience = (audience, contacts = []) => {
  if (!audience || audience.type === 'all') return contacts;
  
  if (audience.type === 'filtered') {
    return contacts.filter(contact => {
      return audience.filters?.every(filter => {
        const fieldValue = contact[filter.field] || '';
        const filterValue = filter.value.toLowerCase();
        const contactValue = fieldValue.toString().toLowerCase();

        switch (filter.condition) {
          case 'contains':
            return contactValue.includes(filterValue);
          case 'equals':
            return contactValue === filterValue;
          case 'startsWith':
            return contactValue.startsWith(filterValue);
          case 'endsWith':
            return contactValue.endsWith(filterValue);
          case 'isEmpty':
            return !contactValue;
          case 'isNotEmpty':
            return !!contactValue;
          default:
            return true;
        }
      });
    });
  }
  
  if (audience.type === 'segments') {
    // Mock segment filtering - in real app this would be more sophisticated
    return contacts.filter(contact => {
      return audience.segments?.some(segment => {
        // Mock logic based on segment names
        if (segment === 'VIP Customers') return contact.vip === true;
        if (segment === 'New Subscribers') return new Date(contact.dateJoined) > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
        if (segment === 'High Engagement') return contact.engagementScore > 7;
        return false;
      });
    });
  }
  
  return contacts;
};

export const calculateEstimatedReach = (campaign, contacts = []) => {
  const targetContacts = processAudience(campaign.audience, contacts);
  return targetContacts.length;
};

const DEFAULT_SENDER_NAME = 'Your Company';
const DEFAULT_SENDER_EMAIL = 'hello@yourcompany.com';

export const transformCampaignRecord = (campaign) => {
  const scheduledAt = deriveScheduledAt(campaign);
  const sentAt = deriveSentAt(campaign);
  const schedule = campaign.scheduling
    ? transformBackendScheduleToFrontend(campaign.scheduling)
    : (campaign.schedule || { sendOption: 'now' });

  // Prefer audience from backend; fall back to filters (backend field)
  const audienceResolved = campaign.audience || campaign.filters || { type: 'all', filters: [], segments: [], sourceTables: [] };
  
  // Normalize audience.type when backend omitted it
  const normalizeAudience = (aud) => {
    const a = { ...(aud || {}) };
    const t = (a.type || '').toLowerCase();
    if (!t) {
      if (Array.isArray(a.segments) && a.segments.length > 0) a.type = 'segments';
      else if (Array.isArray(a.filters) && a.filters.length > 0) a.type = 'filtered';
      else a.type = 'all';
    }
    return a;
  };

  const statistics = campaign.statistics ? {
    totalRecipients: campaign.statistics.totalRecipients || 0,
    sent: campaign.statistics.sent || 0,
    delivered: campaign.statistics.delivered || 0,
    opened: campaign.statistics.opened || campaign.statistics.opens || 0,
    clicked: campaign.statistics.clicked || campaign.statistics.clicks || 0,
    bounced: campaign.statistics.bounced || campaign.statistics.bounces || 0,
    complained: campaign.statistics.complained || campaign.statistics.blocked || campaign.statistics.blocks || 0,
    unsubscribed: campaign.statistics.unsubscribed || 0,
    // Add aliases for frontend compatibility
    uniqueOpens: campaign.statistics.opened || campaign.statistics.opens || 0,
    uniqueClicks: campaign.statistics.clicked || campaign.statistics.clicks || 0,
    blocked: campaign.statistics.complained || campaign.statistics.blocked || campaign.statistics.blocks || 0,
    bounces: campaign.statistics.bounced || campaign.statistics.bounces || 0
  } : undefined;

  const metrics = campaign.statistics ? {
    sent: campaign.statistics.sent || 0,
    // Surfaced on metrics (not just statistics) because the list reads through
    // metricValue(), which only looks at the row and row.metrics.
    delivered: campaign.statistics.delivered || 0,
    totalRecipients: campaign.statistics.totalRecipients || 0,
    opens: campaign.statistics.opened || campaign.statistics.opens || 0,
    clicks: campaign.statistics.clicked || campaign.statistics.clicks || 0,
    bounces: campaign.statistics.bounced || campaign.statistics.bounces || 0,
    blocks: campaign.statistics.complained || campaign.statistics.blocked || campaign.statistics.blocks || 0,
    replies: campaign.statistics.replied || campaign.statistics.replies || 0,
    conversions: campaign.statistics.conversions || 0,
    uniqueOpens: campaign.statistics.opened || campaign.statistics.opens || 0,
    uniqueClicks: campaign.statistics.clicked || campaign.statistics.clicks || 0
  } : (campaign.metrics || { sent: 0, delivered: 0, totalRecipients: 0, opens: 0, clicks: 0, bounces: 0, blocks: 0, replies: 0, conversions: 0 });
  const capitalize = (str) => {
    if (!str) return 'Draft';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const campaignId = String(campaign._id || campaign.id || '');
  return {
    id: campaignId,
    _id: campaignId,
    // Each step carries its own template; the Content tab lists them.
    sequenceSteps: Array.isArray(campaign.sequenceSteps) ? campaign.sequenceSteps : [],
    name: campaign.name || 'Untitled Campaign',
    subject: campaign.subject || '',
    preheader: campaign.preheader || campaign.settings?.preheader || campaign.preheader || '',
    fromName: campaign.fromName || campaign.sender?.name || campaign.settings?.fromName || DEFAULT_SENDER_NAME,
    fromEmail: campaign.fromEmail || campaign.sender?.email || campaign.settings?.fromEmail || DEFAULT_SENDER_EMAIL,
    content: campaign.content || '',
    audience: normalizeAudience(audienceResolved),
    // Every audience entry on the campaign. Campaigns saved before multiple audiences
    // existed have none, so the single legacy selection stands in as entry one.
    audiences: Array.isArray(campaign.audiences) && campaign.audiences.length
      ? campaign.audiences.map((entry) => ({
        ...normalizeAudience(entry),
        id: String(entry._id || entry.id || ''),
        name: entry.name || 'Custom Matching Rules',
        estimatedCount: entry.estimatedCount || 0
      }))
      : [],
    abTest: campaign.abTest || campaign.abtest || { enabled: false },
    schedule,
    status: capitalize(campaign.status),
    createdAt: campaign.createdAt || new Date().toISOString(),
    createdBy: campaign.createdBy || 'system',
    sentAt,
    scheduledAt,
    templateId: campaign.templateId || campaign.template || null,
    // Kept on the record so sub-campaigns can offer the campaign's own people to pick from.
    recipients: Array.isArray(campaign.recipients) ? campaign.recipients : [],
    statistics,
    metrics,
    bounces: metrics.bounces,
    blocks: metrics.blocks,
    replies: metrics.replies,
    opens: metrics.opens,
    clicks: metrics.clicks
  };
};
